<script setup lang="ts">
import { ref, onMounted } from 'vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import SelectSearch from '@/components/v2/SelectSearch.vue';
import SICCodesAutocompleteInput from '@/components/SICCodesAutocompleteInput.vue';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import DatePicker from '@/components/DatePicker.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Toggle from '@/components/v2/Toggle.vue';
import AppModal from '@/components/v2/AppModal.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';
import ActivityIndicator from '@/components/v2/ActivityIndicator.vue';
import PagingControl from '@/components/v2/PagingControl.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import TextField from '@/components/v2/TextField.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import RadioGroup, { type RadioOption } from '@/components/v2/RadioGroup.vue';
import Alert from '@/components/v2/Alert.vue';
import Spinner from '@/components/v2/Spinner.vue';
import ProgressBar from '@/components/v2/Progress.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import Tabs, { type TabItem } from '@/components/v2/Tabs.vue';
import Toast from '@/components/v2/Toast.vue';
import { useToast } from '@/composables/useToast';
import { Label } from 'reka-ui';
import { Info, CheckCircle, AlertCircle, AlertTriangle } from '@lucide/vue';
import { type DateValue } from '@internationalized/date';
import type { DateChangePayload } from '@/types/components/DateChangePayload';
import { basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter } from '@codemirror/lint';
import Button from '@/components/ui/UIButton.vue';

// ** refs **
const codeMirrorRef = ref<HTMLElement | null>(null);
const editorView = ref<EditorView>();

// ** lifecycles **
onMounted(() => {
  if (codeMirrorRef.value) {
    const state = EditorState.create({
      doc: '{"one": 1, "two": "two", "three": [1, 2, 3]}',
      extensions: [basicSetup, json(), linter(jsonParseLinter())],
    });
    editorView.value = new EditorView({
      state,
      parent: codeMirrorRef.value,
    });
  }
});

// Example data for AppSelect
const selectOptions = [
  { value: 'option1', label: 'Option 1' },
  { value: 'option2', label: 'Option 2' },
  { value: 'option3', label: 'Option 3' },
];

// Example data for SelectSearch
const searchFields = [
  { value: 'description', label: 'Description' },
  { value: 'policyNumber', label: 'Policy Number' },
  { value: 'accountName', label: 'Account Name' },
];


const selectedValue = ref('Option 1');
const selectedValue2 = ref('Option 1');
const selectedValue3 = ref('Option 1');
const hasError = ref(false);

const searchQuery = ref('');
const searchField = ref('description');

const sicCode = ref<string>('');
const startDate = ref<Date>(new Date());
const endDate = ref<Date>(new Date());
const datePickerDate = ref<DateValue>();

const toggleValue = ref(false);
const isModalOpen = ref(false);
const isConfirmModalOpen = ref(false);
const currentPage = ref(1);
const totalPages = ref(10);

// New component states
const textFieldValue = ref('');
const textFieldError = ref(false);
const checkboxValue = ref(false);
const radioValue = ref('option1');
const radioOptions: RadioOption[] = [
  { label: 'Option 1', value: 'option1' },
  { label: 'Option 2', value: 'option2' },
  { label: 'Option 3', value: 'option3' },
];
const progressValue = ref(45);

function handleDateApply(payload: DateChangePayload) {
  if (payload.startDate) startDate.value = payload.startDate;
  if (payload.endDate) endDate.value = payload.endDate;
}
const currentTab = ref('tab1');
const tabItems: TabItem[] = [
  { value: 'tab1', label: 'Tab 1' },
  { value: 'tab2', label: 'Tab 2' },
  { value: 'tab3', label: 'Tab 3' },
];
const { success, error, warning, info } = useToast();

function handlePageChange(page: number) {
  currentPage.value = page;
}

function handleModalCancel() {
  isModalOpen.value = false;
}

function handleModalConfirm() {
  isModalOpen.value = false;
}

function handleConfirmModalCancel() {
  isConfirmModalOpen.value = false;
}

function handleConfirmModalConfirm() {
  isConfirmModalOpen.value = false;
}

function handleFormat() {
  const output = editorView.value?.state.doc.toString() ?? '';
  let result = output;
  try {
    result = JSON.stringify(JSON.parse(output), null, 2);
  } catch (e) {
    console.error(e);
  }
  editorView.value?.dispatch({
    changes: { from: 0, to: editorView.value.state.doc.length, insert: result },
  });
}
</script>

<template>
  <div class="container mx-auto p-8">
    <h1 class="text-2xl font-bold mb-8">Custom Components</h1>

    <!-- CodeMirror -->
    <section class="mb-12">
      <h2 class="mb-4 text-xl font-semibold">CodeMirror</h2>
      <p class="mb-4 text-gray-600">CodeMirror editor</p>

      <div class="flex flex-col gap-4">
        <div class="rounded-lg border p-4">
          <div ref="codeMirrorRef" class="flex flex-col gap-4"></div>
        </div>
        <Button class="w-fit" size="sm" @click="handleFormat">Format</Button>
      </div>
    </section>

    <!-- AppSelect Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">AppSelect</h2>
      <p class="text-gray-600 mb-4">
        A custom select component that replaces native select elements while maintaining the same functionality.
        It supports v-model binding and can be configured with or without a border. The component offers multiple
        ways to implement the select functionality:
      </p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Simple Usage with Options Prop -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Simple Usage</h3>
          <p class="text-sm text-gray-500 mb-2">Using the options prop for basic implementation</p>
          <AppSelect
            v-model="selectedValue"
            :options="selectOptions"
          />
        </div>

        <!-- Custom Trigger with Options Prop -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Custom Trigger</h3>
          <p class="text-sm text-gray-500 mb-2">Customizing the trigger while using options prop</p>
          <AppSelect
            v-model="selectedValue2"
            :options="selectOptions"
            noBorder
          >
            <template #trigger="{ value }">
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ value }}</span>
                <span class="text-sm text-gray-500">(Custom Trigger)</span>
              </div>
            </template>
          </AppSelect>
        </div>

        <!-- Fully Custom Implementation -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Fully Custom</h3>
          <p class="text-sm text-gray-500 mb-2">Complete control over both trigger and menu</p>
          <AppSelect v-model="selectedValue3">
            <template #trigger="{ value }">
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ value }}</span>
                <span class="text-sm text-gray-500">(Custom)</span>
              </div>
            </template>
            <template #menu="{ select, value }">
              <div class="py-1">
                <div
                  v-for="option in selectOptions"
                  :key="option.value"
                  class="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                  @click="select(option.value)"
                >
                  <span>{{ option.label }}</span>
                  <span v-if="option.value === value" class="text-success">✓</span>
                </div>
              </div>
            </template>
          </AppSelect>
        </div>

        <!-- Error State -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Error State</h3>
          <p class="text-sm text-gray-500 mb-2">Select with error styling</p>
          <div class="flex items-center gap-4">
            <AppSelect
              v-model="selectedValue3"
              :options="selectOptions"
              :error="hasError"
              class="w-48"
            />
            <AppButton @click="hasError = !hasError">
              {{ hasError ? 'Clear Error' : 'Show Error' }}
            </AppButton>
          </div>
        </div>
      </div>
    </section>

    <!-- AppButton Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">AppButton</h2>
      <p class="text-gray-600 mb-4">
        Custom button component with consistent styling and multiple variants.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Variants</h3>
          <p class="text-sm text-gray-500 mb-2">Different button styles</p>
          <div class="flex gap-4">
            <AppButton>Default</AppButton>
            <AppButton primary>Primary</AppButton>
            <AppButton disabled>Disabled</AppButton>
          </div>
        </div>
      </div>
    </section>

    

    <!-- Date Components Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Date Components</h2>
      <p class="text-gray-600 mb-4">
        A collection of date-related components for handling date selection and ranges.
      </p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- DatePicker -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">DatePicker</h3>
          <p class="text-sm text-gray-500 mb-2">Single date selection component</p>
          <div class="flex flex-col gap-2">
            <Label class="text-sm" for="date-field">Select Date</Label>
            <DatePicker v-model="datePickerDate" />
          </div>
        </div>

        <!-- ManagedDateRange -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">ManagedDateRange</h3>
          <p class="text-sm text-gray-500 mb-2">Date range selection with preset options</p>
          <ManagedDateRange
            :initial-start-date="startDate"
            :initial-end-date="endDate"
            :on-apply="handleDateApply"
          />
        </div>
      </div>
    </section>

    <!-- Modal Components Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Modal Components</h2>
      <p class="text-gray-600 mb-4">
        Modal components for displaying content in overlays.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- AppModal -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">AppModal</h3>
          <p class="text-sm text-gray-500 mb-2">Base modal component</p>
          <AppButton @click="isModalOpen = true">Open Modal</AppButton>
          <AppModal
            :show="isModalOpen"
            title="Example Modal"
            :handleCancel="handleModalCancel"
            :handleConfirm="handleModalConfirm">
            <p>This is an example modal content.</p>
          </AppModal>
        </div>

        <!-- ConfirmModal -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">ConfirmModal</h3>
          <p class="text-sm text-gray-500 mb-2">Confirmation dialog component</p>
          <AppButton @click="isConfirmModalOpen = true">Open Confirm Modal</AppButton>
          <ConfirmModal
            :show="isConfirmModalOpen"
            title="Confirm Action"
            message="Are you sure you want to proceed?"
            :handleCancel="handleConfirmModalCancel"
            :handleConfirm="handleConfirmModalConfirm"
            cancelButtonLabel="No, cancel"
            actionButtonLabel="Yes, proceed" />
        </div>
      </div>
    </section>

    <!-- PagingControl Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">PagingControl</h2>
      <p class="text-gray-600 mb-4">
        Pagination component for navigating through pages of content.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Default Usage</h3>
          <p class="text-sm text-gray-500 mb-2">Basic pagination with page change handling</p>
          <PagingControl
            :currentPage="currentPage"
            :totalPages="totalPages"
            @set-page="handlePageChange"
          />
        </div>
      </div>
    </section>

    <!-- SelectSearch Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">SelectSearch</h2>
      <p class="text-gray-600 mb-4">
        A combination of AppSelect and search input that allows users to select a search field and enter search terms.
        The component supports debounced search and clear functionality.
      </p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Default Usage -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Default Usage</h3>
          <p class="text-sm text-gray-500 mb-2">Basic implementation with search field selection and text input</p>
          <SelectSearch
            v-model:query="searchQuery"
            v-model:selected-option="searchField"
            :options="searchFields"
            class="w-full"
          />
        </div>

        <!-- Current Values Display -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Current Values</h3>
          <p class="text-sm text-gray-500 mb-2">Live display of component state</p>
          <div class="bg-gray-50 p-4 rounded">
            <p class="mb-2"><span class="font-medium">Selected Field:</span> {{ searchField }}</p>
            <p><span class="font-medium">Search Query:</span> {{ searchQuery }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SIC Codes Autocomplete Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">SIC Codes Autocomplete</h2>
      <p class="text-gray-600 mb-4">
        A specialized autocomplete component for searching and selecting SIC codes.
      </p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Default Usage -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Default Usage</h3>
          <p class="text-sm text-gray-500 mb-2">Basic implementation</p>
          <SICCodesAutocompleteInput
            v-model="sicCode"
            class="w-full"
          />
        </div>

        <!-- Current Value Display -->
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Current Value</h3>
          <p class="text-sm text-gray-500 mb-2">Live display of component state</p>
          <div class="bg-gray-50 p-4 rounded">
            <p><span class="font-medium">Selected SIC Code:</span> {{ sicCode }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Toggle Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Toggle</h2>
      <p class="text-gray-600 mb-4">
        Toggle switch component for binary state selection.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Default Usage</h3>
          <p class="text-sm text-gray-500 mb-2">Basic toggle with label</p>
          <div class="flex items-center gap-4">
            <Toggle
              id="example-toggle"
              label="Example Toggle"
              v-model="toggleValue"
            />
            <span>Current State: {{ toggleValue ? 'On' : 'Off' }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ActivityIndicator Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">ActivityIndicator</h2>
      <p class="text-gray-600 mb-4">
        Loading spinner component that shows activity state.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">States</h3>
          <p class="text-sm text-gray-500 mb-2">Active and inactive states</p>
          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <ActivityIndicator :lastActiveDate="new Date()" />
              <span>Active State</span>
            </div>
            <div class="flex items-center gap-2">
              <ActivityIndicator :lastActiveDate="new Date(Date.now() - 1000 * 60 * 5)" />
              <span>Inactive State (5 minutes ago)</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Skeleton Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Skeleton</h2>
      <p class="text-gray-600 mb-4">
        Loading skeleton component for placeholder content.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Shapes</h3>
          <p class="text-sm text-gray-500 mb-2">Different shapes: text, circle, rectangle</p>
          <div class="flex flex-col gap-4">
            <Skeleton shape="text" size="md" />
            <Skeleton shape="circle" size="md" />
            <Skeleton shape="rectangle" size="md" />
          </div>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Sizes</h3>
          <p class="text-sm text-gray-500 mb-2">Different sizes: sm, md, lg</p>
          <div class="flex flex-col gap-4">
            <Skeleton shape="text" size="sm" />
            <Skeleton shape="text" size="md" />
            <Skeleton shape="text" size="lg" />
          </div>
        </div>
      </div>
    </section>

    <!-- TextField Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">TextField</h2>
      <p class="text-gray-600 mb-4">
        Text input component with label, error, and hint support.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Normal State</h3>
          <p class="text-sm text-gray-500 mb-2">Basic text field with label</p>
          <TextField
            v-model="textFieldValue"
            label="Name"
            placeholder="Enter your name"
          />
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Error State</h3>
          <p class="text-sm text-gray-500 mb-2">Text field with error message</p>
          <TextField
            v-model="textFieldValue"
            label="Email"
            placeholder="Enter your email"
            :error="textFieldError"
            error-message="Please enter a valid email"
          />
          <AppButton class="mt-2" @click="textFieldError = !textFieldError">
            {{ textFieldError ? 'Clear Error' : 'Show Error' }}
          </AppButton>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Disabled & Readonly</h3>
          <p class="text-sm text-gray-500 mb-2">Disabled and readonly states</p>
          <div class="flex flex-col gap-4">
            <TextField
              v-model="textFieldValue"
              label="Disabled"
              value="Cannot edit"
              disabled
            />
            <TextField
              v-model="textFieldValue"
              label="Readonly"
              value="Read only value"
              readonly
            />
          </div>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">With Hint</h3>
          <p class="text-sm text-gray-500 mb-2">Text field with hint text</p>
          <TextField
            v-model="textFieldValue"
            label="Password"
            type="password"
            hint="Must be at least 8 characters"
          />
        </div>
      </div>
    </section>

    <!-- Checkbox Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Checkbox</h2>
      <p class="text-gray-600 mb-4">
        Checkbox component with label and states.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Normal State</h3>
          <p class="text-sm text-gray-500 mb-2">Basic checkbox with label</p>
          <Checkbox
            v-model="checkboxValue"
            label="Accept terms and conditions"
          />
          <p class="mt-2 text-sm">Value: {{ checkboxValue }}</p>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Disabled State</h3>
          <p class="text-sm text-gray-500 mb-2">Disabled checkbox</p>
          <Checkbox
            label="Cannot change this"
            :model-value="true"
            disabled
          />
        </div>
      </div>
    </section>

    <!-- RadioGroup Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">RadioGroup</h2>
      <p class="text-gray-600 mb-4">
        Radio button group component.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Vertical Layout</h3>
          <p class="text-sm text-gray-500 mb-2">Default vertical layout</p>
          <RadioGroup
            v-model="radioValue"
            :options="radioOptions"
            layout="vertical"
          />
          <p class="mt-2 text-sm">Selected: {{ radioValue }}</p>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Horizontal Layout</h3>
          <p class="text-sm text-gray-500 mb-2">Horizontal radio group</p>
          <RadioGroup
            v-model="radioValue"
            :options="radioOptions"
            layout="horizontal"
          />
        </div>
      </div>
    </section>

    <!-- Alert Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Alert</h2>
      <p class="text-gray-600 mb-4">
        Alert component with different types and states.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Types</h3>
          <p class="text-sm text-gray-500 mb-2">All alert types</p>
          <div class="flex flex-col gap-4">
            <Alert type="info" title="Info" :icon="Info">
              This is an info alert.
            </Alert>
            <Alert type="success" title="Success" :icon="CheckCircle">
              This is a success alert.
            </Alert>
            <Alert type="warning" title="Warning" :icon="AlertTriangle">
              This is a warning alert.
            </Alert>
            <Alert type="error" title="Error" :icon="AlertCircle">
              This is an error alert.
            </Alert>
          </div>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Closable</h3>
          <p class="text-sm text-gray-500 mb-2">Alert with close button</p>
          <Alert
            type="info"
            title="Closable Alert"
            closable
            @close="() => {}"
          >
            Click the X to close this alert.
          </Alert>
        </div>
      </div>
    </section>

    <!-- Spinner Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Spinner</h2>
      <p class="text-gray-600 mb-4">
        Loading spinner component with different sizes and types.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Sizes</h3>
          <p class="text-sm text-gray-500 mb-2">Different sizes: xs, sm, md, lg</p>
          <div class="flex items-center gap-4">
            <Spinner size="xs" />
            <Spinner size="sm" />
            <Spinner size="md" />
            <Spinner size="lg" />
          </div>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Types</h3>
          <p class="text-sm text-gray-500 mb-2">Different spinner types</p>
          <div class="flex items-center gap-4">
            <Spinner type="spinner" />
            <Spinner type="dots" />
            <Spinner type="ring" />
          </div>
        </div>
      </div>
    </section>

    <!-- Progress Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Progress</h2>
      <p class="text-gray-600 mb-4">
        Progress bar component with value and indeterminate states.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">With Value</h3>
          <p class="text-sm text-gray-500 mb-2">Progress bar with specific value</p>
          <ProgressBar :value="progressValue" :max="100" />
          <p class="mt-2 text-sm">Value: {{ progressValue }}%</p>
          <div class="flex gap-2 mt-2">
            <AppButton @click="progressValue = Math.max(0, progressValue - 10)">-10%</AppButton>
            <AppButton @click="progressValue = Math.min(100, progressValue + 10)">+10%</AppButton>
          </div>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Indeterminate</h3>
          <p class="text-sm text-gray-500 mb-2">Indeterminate progress bar</p>
          <ProgressBar :indeterminate="true" />
        </div>
      </div>
    </section>

    <!-- Tooltip Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Tooltip</h2>
      <p class="text-gray-600 mb-4">
        Tooltip component with different positions.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Positions</h3>
          <p class="text-sm text-gray-500 mb-2">Hover to see tooltips</p>
          <div class="flex flex-wrap gap-4 justify-center">
            <Tooltip text="Top tooltip" position="top">
              <AppButton>Top</AppButton>
            </Tooltip>
            <Tooltip text="Bottom tooltip" position="bottom">
              <AppButton>Bottom</AppButton>
            </Tooltip>
            <Tooltip text="Left tooltip" position="left">
              <AppButton>Left</AppButton>
            </Tooltip>
            <Tooltip text="Right tooltip" position="right">
              <AppButton>Right</AppButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </section>

    <!-- Tabs Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Tabs</h2>
      <p class="text-gray-600 mb-4">
        Tabs component with different styles.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Bordered Style</h3>
          <p class="text-sm text-gray-500 mb-2">Default bordered tabs</p>
          <Tabs v-model="currentTab" :items="tabItems" variant="bordered">
            <template #tab1>
              <p>Content for Tab 1</p>
            </template>
            <template #tab2>
              <p>Content for Tab 2</p>
            </template>
            <template #tab3>
              <p>Content for Tab 3</p>
            </template>
          </Tabs>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Lifted Style</h3>
          <p class="text-sm text-gray-500 mb-2">Lifted tabs style</p>
          <Tabs v-model="currentTab" :items="tabItems" variant="lifted">
            <template #tab1>
              <p>Content for Tab 1</p>
            </template>
            <template #tab2>
              <p>Content for Tab 2</p>
            </template>
            <template #tab3>
              <p>Content for Tab 3</p>
            </template>
          </Tabs>
        </div>
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Boxed Style</h3>
          <p class="text-sm text-gray-500 mb-2">Boxed tabs style</p>
          <Tabs v-model="currentTab" :items="tabItems" variant="boxed">
            <template #tab1>
              <p>Content for Tab 1</p>
            </template>
            <template #tab2>
              <p>Content for Tab 2</p>
            </template>
            <template #tab3>
              <p>Content for Tab 3</p>
            </template>
          </Tabs>
        </div>
      </div>
    </section>

    <!-- Toast Section -->
    <section class="mb-12">
      <h2 class="text-xl font-semibold mb-4">Toast</h2>
      <p class="text-gray-600 mb-4">
        Toast notification system with different types.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-4 border border-base-content/15 rounded-box">
          <h3 class="font-medium mb-2">Toast Types</h3>
          <p class="text-sm text-gray-500 mb-2">Click buttons to show toasts</p>
          <div class="flex flex-col gap-2">
            <AppButton @click="() => success('Operation completed successfully!')">
              Show Success Toast
            </AppButton>
            <AppButton @click="() => error('Something went wrong!')">
              Show Error Toast
            </AppButton>
            <AppButton @click="() => warning('Please review this action')">
              Show Warning Toast
            </AppButton>
            <AppButton @click="() => info('Here is some information')">
              Show Info Toast
            </AppButton>
          </div>
        </div>
      </div>
      <Toast />
    </section>
  </div>
</template>
